import { useState, type FormEvent, type JSX, type MouseEvent } from "react";
import { Ikone } from "../Ikone.tsx";

/** Der Koerper, den `changeOwnPassword` an `/api/auth/password` schickt (index.html:5768-5771). */
export interface PasswortDialogDaten {
  currentPassword: string;
  newPassword: string;
}

export interface PasswortDialogProps {
  /** `changeOwnPassword` NACH bestandener Client-Pruefung (index.html:5767) - ohne den API-Aufruf selbst, siehe Kopfkommentar. */
  onSpeichern: (daten: PasswortDialogDaten) => void;
  /** `closePasswordDialog` (index.html:5748-5750). */
  onSchliessen: () => void;
  /**
   * Serverfehler, den `changeOwnPassword` nach einem fehlgeschlagenen API-Aufruf ebenfalls in
   * `#passwordError` schreibt (index.html:5773-5776: `data.error || '...'`). Der eigentliche
   * `fetch`-Aufruf ist Behaelterarbeit (siehe `ZahlungsSeite.tsx`, "Was NICHT hierher gehoert") und
   * liegt ausserhalb dieser Komponente; optional, damit der im Task-Brief gezeigte Aufruf mit genau
   * zwei Props unveraendert uebersetzt.
   */
  serverFehler?: string;
}

/**
 * `#passwordDialogOverlay`/`.password-dialog` (index.html:3737-3759), Verhalten aus
 * `openPasswordDialog`/`closePasswordDialog`/`changeOwnPassword` (index.html:5737-5780).
 *
 * BEFUND am Task-Brief (Schritt 2, "verlangt beim Passwort mindestens acht Zeichen"): der Brief
 * erwartet einen ueber die Eingabelaenge DEAKTIVIERTEN Speichern-Knopf. Der Bestand tut das nicht -
 * `changeOwnPassword` prueft erst BEIM ABSENDEN (`event.preventDefault()`, dann die Laenge, dann den
 * Abgleich) und zeigt bei Fehlschlag einen Text in `.password-error` (`role="alert"`,
 * index.html:3752); der Knopf selbst bleibt immer klickbar. Diese Komponente uebernimmt das
 * WORTGLEICHE Bestandsverhalten (Submit-Validierung, Fehlertext, Knopf bleibt aktiv) statt der vom
 * Brief erfundenen Deaktivierung - siehe Task-Bericht.
 *
 * Nur der `onSchliessen`-Aufruf beim Klick auf den Hintergrund (`if(event.target===this)`,
 * index.html:3737) ist woertlich mitgenommen: ein Klick auf die Kachel selbst darf nicht schliessen.
 */
export function PasswortDialog({ onSpeichern, onSchliessen, serverFehler }: PasswortDialogProps): JSX.Element {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fehler, setFehler] = useState("");

  function absenden(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setFehler("");
    // index.html:5759-5766, in dieser Reihenfolge.
    if (newPassword.length < 8) {
      setFehler("Das neue Passwort muss mindestens 8 Zeichen lang sein.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setFehler("Die beiden neuen Passwörter stimmen nicht überein.");
      return;
    }
    onSpeichern({ currentPassword, newPassword });
  }

  function hintergrundKlick(event: MouseEvent<HTMLDivElement>): void {
    if (event.target === event.currentTarget) onSchliessen();
  }

  return (
    <div className="board-editor-overlay open" role="presentation" onClick={hintergrundKlick}>
      <section
        className="board-editor-dialog password-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="passwordDialogTitle"
      >
        <div className="board-editor-head">
          <div className="board-editor-head-copy">
            <h2 id="passwordDialogTitle">Eigenes Passwort ändern</h2>
            <p>Das neue Passwort muss mindestens acht Zeichen lang sein.</p>
          </div>
          <button className="icon-btn" type="button" onClick={onSchliessen} title="Dialog schließen">
            <Ikone name="x" />
          </button>
        </div>
        <form className="password-form" onSubmit={absenden}>
          <div className="password-fields">
            <label>
              Aktuelles Passwort
              <input
                type="password"
                autoComplete="current-password"
                required
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
              />
            </label>
            <label>
              Neues Passwort
              <input
                type="password"
                autoComplete="new-password"
                minLength={8}
                required
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
              />
            </label>
            <label>
              Neues Passwort wiederholen
              <input
                type="password"
                autoComplete="new-password"
                minLength={8}
                required
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
              />
            </label>
          </div>
          <div className="password-error" role="alert">
            {serverFehler || fehler}
          </div>
          <div className="board-editor-foot">
            <button className="btn-sec" type="button" onClick={onSchliessen}>
              <Ikone name="x" />
              Abbrechen
            </button>
            <button className="btn-primary" type="submit">
              <Ikone name="save" />
              Passwort speichern
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
